import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import {
  UNKNOWN_ALLOWANCE,
  creditStateMeta,
  isModelDegraded,
  normalizeAiAllowance,
  resolveCreditState,
  type AiAllowance,
  type AiCreditState,
  type CreditStateMeta,
} from '@/lib/ai/credit-state';

/**
 * The AI allowance for the CURRENT workspace.
 *
 * Producer: `GET /v1/me/ai-allowance`.
 *
 * ── WORKSPACE-KEYED, and this is not incidental ─────────────────────────────
 * `hooks/settings-ai-studio/useUsageSummary.ts:29` keys its cache on a flat
 * `['ai-studio', 'usage-summary']`. Switch workspace and TanStack serves the
 * previous tenant's usage figures out of cache until the staleTime expires —
 * on an MSSP console that is one tenant's ceiling shown against another
 * tenant's name. That bug is NOT copied here: the workspace id is part of the
 * key, so a switch is a different cache entry and cannot be answered by a
 * stale one.
 *
 * ── FAILS OPEN, to `unknown` — never to `healthy` ───────────────────────────
 * Three distinct ways to have no answer, all of which land on the same honest
 * state:
 *   - still in flight,
 *   - the request threw (network, 404 because the producer is not deployed
 *     yet, 500),
 *   - the response was not an allowance shape at all.
 *
 * `unknown` renders neutrally everywhere (see `CREDIT_STATE_META`) and
 * `isBlocked` stays FALSE, so a probe that cannot answer never hides a working
 * feature — the same fail-open reasoning as
 * `hooks/ai-studio/use-ai-studio-availability.ts`. Nothing is bypassed by
 * being optimistic: the API enforces the ceiling server-side regardless of
 * what this hook believes.
 *
 * What it must NOT do is fail CLOSED-but-cheerful. Returning `healthy` on
 * error would be the false green the observability postmortem named — absence
 * of bad news is not good news.
 *
 * ── Non-null by construction ────────────────────────────────────────────────
 * `allowance` is always a complete `AiAllowance`, never `undefined`, because
 * `apps/web/CLAUDE.md` bans `?? []` / `?? {}` in pages and components. All the
 * nullish handling happens in `normalizeAiAllowance` at this layer, so every
 * consumer can write `allowance.remedies.map(...)` without a guard.
 */
export const aiAllowanceKeys = {
  all: () => ['ai', 'allowance'] as const,
  detail: (workspaceId: string | null) =>
    [...aiAllowanceKeys.all(), workspaceId ?? 'no-workspace'] as const,
};

export interface UseAiCreditsOptions {
  /**
   * May this consumer TRIGGER the request? Default `true`.
   *
   * `false` makes the hook read-only: it serves whatever a real AI surface has
   * already put in the cache and issues no request of its own. That exists for
   * chrome — `components/ai-chat/header-ask-ai-button.tsx` is mounted on EVERY
   * authenticated page via `components/header-controls.tsx`, and
   * `components/ai-chat/message-actions.tsx` renders once per transcript row.
   * Wiring either of them the obvious way would put a 60-second poll behind the
   * whole product for controls that spend nothing.
   *
   * A cold cache then reads `unknown`, which is the honest answer and renders
   * neutrally — it is NOT permission to render healthy, and it still never
   * blocks.
   */
  enabled?: boolean;
}

export interface UseAiCreditsResult {
  /** Always a complete object. `UNKNOWN_ALLOWANCE` when we have no answer. */
  allowance: AiAllowance;
  /** The reconciled state — the one literal every surface renders from. */
  state: AiCreditState;
  /** Styling + label for `state`. */
  meta: CreditStateMeta;
  /**
   * True ONLY when the server both says the ceiling is reached AND says it is
   * enforcing it. Observe mode (`enforcing: false`) measures without blocking,
   * and `unknown` never blocks — see the fail-open note.
   */
  isBlocked: boolean;
  /**
   * True when a run came back at a lower tier than requested. Read off
   * `modelTier`, deliberately NOT off `state`, so a downgrade is still
   * announced when the credit ladder has escalated past `degraded`.
   */
  isDegraded: boolean;
  isLoading: boolean;
  /** True when we are showing `UNKNOWN_ALLOWANCE` because the probe failed. */
  isUnavailable: boolean;
  refetch: () => void;
}

export function useAiCredits(
  options: UseAiCreditsOptions = {},
): UseAiCreditsResult {
  // Wave-3 item 4 (audit 2026-09-25) — keyed by the LIVE workspace (the one
  // x-workspace-id follows). The auth store's workspaceId is the login-time
  // one, so after a switch this kept the previous workspace's key and never
  // re-read the allowance for the workspace on screen.
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const enabled = options.enabled !== false;

  const query = useQuery({
    queryKey: aiAllowanceKeys.detail(workspaceId),
    queryFn: () => fetchApi<unknown>('/me/ai-allowance'),
    // A credit ceiling moves while you generate; a minute is short enough to
    // notice the threshold being crossed and long enough not to hammer.
    staleTime: 60_000,
    // A read-only observer must not keep a poll alive either — otherwise
    // `enabled: false` would only stop the FIRST request.
    refetchInterval: enabled ? 60_000 : false,
    enabled,
    // No retry: a failure here is not worth three round trips, because failing
    // open to `unknown` is a perfectly serviceable answer.
    retry: false,
  });

  // A successful response can still be garbage (a proxy's HTML error page, a
  // contract drift). normalizeAiAllowance returns UNKNOWN_ALLOWANCE for
  // anything that is not an object, so `hasAnswer` is about the DATA, not
  // merely about the absence of a thrown error.
  const allowance = normalizeAiAllowance(query.data);
  const isUnavailable = query.isError || allowance === UNKNOWN_ALLOWANCE;

  const state = resolveCreditState(allowance);

  return {
    allowance,
    state,
    meta: creditStateMeta(state),
    isBlocked: state === 'exhausted' && allowance.enforcing,
    isDegraded: isModelDegraded(allowance.modelTier),
    isLoading: query.isLoading,
    isUnavailable,
    refetch: () => {
      void query.refetch();
    },
  };
}
